/* IdenScan homepage — one motion/interaction system.
   - Section reveals (IntersectionObserver), respects prefers-reduced-motion.
   - Hero staged load.
   - Audience tab switch (functional; works even under reduced motion).
   - Product-story active-step highlight on scroll.
   Progressive enhancement: without JS, content stays fully visible and readable. */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  function initTabs() {
    var groups = document.querySelectorAll('[data-ids-tabs]');
    groups.forEach(function (group) {
      var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-ids-tab]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[data-ids-panel]'));
      var stage = group.querySelector('.ids-h-stage');
      if (!tabs.length || !panels.length) return;
      if (stage) stage.classList.add('is-enhanced');

      function setInactive(panel, inactive) {
        if (inactive) { panel.setAttribute('aria-hidden', 'true'); if ('inert' in panel) panel.inert = true; }
        else { panel.removeAttribute('aria-hidden'); if ('inert' in panel) panel.inert = false; }
      }

      function render(key, focusTab) {
        if (!group.querySelector('[data-ids-tab="' + key + '"]')) return;
        tabs.forEach(function (tab) {
          var active = tab.getAttribute('data-ids-tab') === key;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
          if (active && focusTab) tab.focus();
        });
        panels.forEach(function (panel) {
          var active = panel.getAttribute('data-ids-panel') === key;
          panel.classList.toggle('is-active', active);
          setInactive(panel, !active);
        });
      }

      // Initial state from the tab marked active in markup (personal default).
      var initial = (group.querySelector('[data-ids-tab].is-active') || tabs[0]).getAttribute('data-ids-tab');
      panels.forEach(function (p) { setInactive(p, p.getAttribute('data-ids-panel') !== initial); });

      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () { render(tab.getAttribute('data-ids-tab'), false); });
        tab.addEventListener('keydown', function (e) {
          var n = index;
          if (e.key === 'ArrowRight') n = (index + 1) % tabs.length;
          else if (e.key === 'ArrowLeft') n = (index - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') n = 0;
          else if (e.key === 'End') n = tabs.length - 1;
          else return;
          e.preventDefault();
          render(tabs[n].getAttribute('data-ids-tab'), true);
        });
      });

      function followHash() {
        if (window.location.hash === '#voor-bedrijven') render('business', false);
        else if (window.location.hash === '#voor-persoonlijk') render('personal', false);
      }
      window.addEventListener('hashchange', followHash);
      followHash();

      // Optional autoplay through the states; stops for good once the visitor interacts.
      if (group.hasAttribute('data-ids-autoplay') && !reduceMotion) {
        var timer = null;
        var stopped = false;
        var next = function () {
          var current = tabs.findIndex(function (t) { return t.classList.contains('is-active'); });
          render(tabs[(current + 1) % tabs.length].getAttribute('data-ids-tab'), false);
        };
        var start = function () { if (!stopped && !timer) timer = window.setInterval(next, 4800); };
        var pause = function () { window.clearInterval(timer); timer = null; };
        var stop = function () { stopped = true; pause(); };
        ['pointerdown', 'keydown', 'focusin'].forEach(function (ev) { group.addEventListener(ev, stop); });
        if (hasIO) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (e) { if (e.isIntersecting) start(); else pause(); });
          }, { threshold: 0.35 }).observe(group);
        } else {
          start();
        }
      }
    });
  }

  function initReveals() {
    if (reduceMotion || !hasIO) return;
    docEl.classList.add('ids-reveal-ready');

    var targets = document.querySelectorAll('[data-ids-reveal], [data-ids-reveal-group], [data-ids-reveal-media]');
    if (targets.length) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      targets.forEach(function (el) { io.observe(el); });
    }

    // Hero staged load
    var hero = document.querySelector('.ids-h-hero');
    if (hero) { window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { hero.classList.add('is-loaded'); }); }); }

    // Safety net after load
    window.addEventListener('load', function () {
      window.setTimeout(function () {
        targets.forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
        });
      }, 200);
    });
  }

  function initStory() {
    if (!hasIO) return;
    var steps = Array.prototype.slice.call(document.querySelectorAll('[data-ids-step]'));
    if (steps.length < 2) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          steps.forEach(function (s) { s.classList.toggle('is-active', s === e.target); });
        }
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    steps.forEach(function (s) { io.observe(s); });
  }

  // Training simulations (hero + training section): pick an action, see the signals and the uitleg.
  function initSims() {
    var cards = document.querySelectorAll('[data-ids-sim-card]');
    cards.forEach(function (card) {
      var answer = card.getAttribute('data-answer');
      var result = card.querySelector('[data-ids-sim-result]');
      var title = result && result.querySelector('strong');
      var picks = Array.prototype.slice.call(card.querySelectorAll('[data-ids-sim-pick]'));
      if (!result || !title || !picks.length) return;

      picks.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var right = btn.getAttribute('data-ids-sim-pick') === answer;
          picks.forEach(function (b) { b.classList.toggle('is-picked', b === btn); });
          card.classList.add('is-done');
          card.classList.toggle('is-right', right);
          card.classList.toggle('is-wrong', !right);
          title.textContent = title.getAttribute(right ? 'data-ok' : 'data-bad');
          result.hidden = false;
        });
      });

      var reset = card.querySelector('[data-ids-sim-reset]');
      if (reset) reset.addEventListener('click', function () {
        card.classList.remove('is-done', 'is-right', 'is-wrong');
        picks.forEach(function (b) { b.classList.remove('is-picked'); });
        result.hidden = true;
        picks[0].focus();
      });
    });
  }

  function init() {
    initTabs();
    initReveals();
    initStory();
    initSims();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
